// Shared constants and helpers for the Engineer Onboarding app.
//
// The process this models: a new engineer is hired, given a 15-day task plan and
// an existing leader as mentor. The engineer logs progress daily; the mentor and
// admin review and comment. At the end of the 15 days a decision is recorded,
// and only once that decision is positive are the contract documents released
// for the engineer to e-sign.

export const ONBOARDING_DAYS = 15;

// ── Journey status pipeline ──────────────────────────────────────────────────
export const JOURNEY_STATUS = {
  active:    { label: 'In progress',     badge: 'sh-badge-info',    desc: 'Working through the 15-day plan' },
  review:    { label: 'Awaiting review', badge: 'sh-badge-warning', desc: 'Plan finished — decision pending' },
  accepted:  { label: 'Accepted',        badge: 'sh-badge-success', desc: 'Confirmed — documents can be released' },
  rejected:  { label: 'Not proceeding',  badge: 'sh-badge-danger',  desc: 'Not moving ahead after the trial' },
  completed: { label: 'Completed',       badge: 'sh-badge-success', desc: 'Onboarding finished and signed off' },
};

// A journey must be in one of these before contract documents may be published.
export const DOCS_RELEASABLE_STATUSES = ['accepted', 'completed'];

// ── Task statuses ───────────────────────────────────────────────────────────
export const TASK_STATUS = {
  'todo':              { label: 'To do',            badge: 'sh-badge-outline' },
  'in-progress':       { label: 'In progress',      badge: 'sh-badge-info' },
  'submitted':         { label: 'Submitted',        badge: 'sh-badge-warning' },
  'changes-requested': { label: 'Changes needed',   badge: 'sh-badge-danger' },
  'approved':          { label: 'Approved',         badge: 'sh-badge-success' },
};

// What the engineer themselves can set (mirrors the API's allow-list).
export const ENGINEER_TASK_STATUSES = ['todo', 'in-progress', 'submitted'];

// ── Final scorecard ─────────────────────────────────────────────────────────
// A structured score beats a free-text-only verdict: it makes the hire/no-hire
// call consistent between engineers and defensible later.
export const SCORECARD_DIMENSIONS = [
  { key: 'technical',     label: 'Technical skill',   hint: 'Code quality, problem solving, depth' },
  { key: 'delivery',      label: 'Delivery & pace',   hint: 'Shipped the tasks at a healthy pace' },
  { key: 'communication', label: 'Communication',     hint: 'Clear updates, asks for help early' },
  { key: 'ownership',     label: 'Ownership',         hint: 'Follows through without being chased' },
  { key: 'culture',       label: 'Team fit',          hint: 'Collaborative, receptive to feedback' },
];

export const RECOMMENDATIONS = {
  hire:    { label: 'Go ahead — confirm',   badge: 'sh-badge-success', journeyStatus: 'accepted' },
  extend:  { label: 'Extend the trial',     badge: 'sh-badge-warning', journeyStatus: 'active' },
  'no-hire': { label: 'Do not proceed',     badge: 'sh-badge-danger',  journeyStatus: 'rejected' },
};

// ── Default 15-day plan ─────────────────────────────────────────────────────
// One click seeds this so a mentor never starts from a blank page. Every item
// is editable afterwards.
export const DEFAULT_PLAN = [
  { day: 1,  title: 'Accounts, access and laptop setup', description: 'Email, Slack, GitHub, cloud console and any internal tools. Confirm you can log in everywhere.' },
  { day: 2,  title: 'Product walkthrough', description: 'Use the product as a customer would. Note anything confusing — fresh eyes are valuable.' },
  { day: 3,  title: 'Run the codebase locally', description: 'Clone the repos, get the app building and running, and the tests passing on your machine.' },
  { day: 4,  title: 'Codebase tour with your mentor', description: 'Walk the main modules, data flow and deployment path with your mentor. Write down the map in your own words.' },
  { day: 5,  title: 'First small fix (good first issue)', description: 'Pick up a small bug, fix it, and open your first pull request.' },
  { day: 6,  title: 'Code review round trip', description: 'Address review feedback and get your first PR merged. Learn our review conventions.' },
  { day: 7,  title: 'Write tests for your change', description: 'Add unit/integration coverage for what you shipped. Learn how we test.' },
  { day: 8,  title: 'Start a small feature slice', description: 'Take a scoped slice of a real feature. Agree the approach with your mentor before coding.' },
  { day: 9,  title: 'Deploy to staging', description: 'Ship your work to staging yourself and learn the release pipeline and rollback.' },
  { day: 10, title: 'Own the feature end to end', description: 'Continue the feature: implementation, tests, docs and review.' },
  { day: 11, title: 'Debug something unfamiliar', description: 'Investigate a real reported issue and write up the root cause.' },
  { day: 12, title: 'Documentation pass', description: 'Improve the docs/README for anything that slowed you down in week one.' },
  { day: 13, title: 'Prepare your demo', description: 'Put together a short demo of what you built and what you learned.' },
  { day: 14, title: 'Demo to the team', description: 'Present your work to the team and gather feedback.' },
  { day: 15, title: 'Retro and wrap-up', description: 'Retro with your mentor: what went well, what was hard, what you need next.' },
];

// ── Date helpers ────────────────────────────────────────────────────────────
export function addDays(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function today() {
  return new Date().toISOString().slice(0, 10);
}

// Which day of the plan we're on (1-based, clamped to the plan length).
// Returns 0 when the start date is missing or still in the future.
export function currentDay(startDate) {
  if (!startDate) return 0;
  const start = new Date(`${startDate}T00:00:00`);
  if (Number.isNaN(start.getTime())) return 0;
  const diff = Math.floor((Date.now() - start.getTime()) / 86400000) + 1;
  if (diff < 1) return 0;
  return Math.min(diff, ONBOARDING_DAYS);
}

export function fmtDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(`${dateStr}T12:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

export function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

// ── Derived progress ────────────────────────────────────────────────────────
export function journeyProgress(tasks) {
  const total = tasks.length;
  const approved = tasks.filter((t) => t.status === 'approved').length;
  const submitted = tasks.filter((t) => t.status === 'submitted').length;
  const percent = total ? Math.round((approved / total) * 100) : 0;
  return { total, approved, submitted, percent };
}

// Days in the elapsed window with no work log — what a mentor most wants to spot.
export function missingLogDays(startDate, logs) {
  const day = currentDay(startDate);
  if (!day) return [];
  const logged = new Set(logs.map((l) => Number(l.day)).filter(Boolean));
  const missing = [];
  for (let d = 1; d <= day; d++) if (!logged.has(d)) missing.push(d);
  return missing;
}
